import { fetchSsrHtml, fetchSsrStyles } from "../../core/common/ssr-utils.js";
import { getPosExtId } from "./utils.js";
import { getSsrBaseUrl } from "./coursesu-env.js";

const PENDING_ATTR = "data-mealz-ssr-pending";
const RECIPE_CARD_HTML_ENDPOINT = "/v3/recipe-card";
const RECIPE_CARD_STYLES_ENDPOINT = "/v3/styles/recipe-card";
const DISPLAY_RECIPE_VARIANT = 2;

let stylesLoaded = false;
let observerStarted = false;
let injectTimeout = null;
let lastHeaders = null;

const htmlByRecipeId = new Map();
const fetchInFlight = new Map();

function getRecipeId(el) {
  return el.getAttribute("recipe-id") || el.recipeId || "";
}

function collectPendingCards() {
  return [
    ...document.querySelectorAll(
      `webc-miam-recipe-card[recipe-id]:not([${PENDING_ATTR}])`
    ),
  ];
}

function markPending(el, recipeId) {
  el.setAttribute(PENDING_ATTR, recipeId);
}

function unmarkPending(el) {
  el.removeAttribute(PENDING_ATTR);
}

function replaceCard(el, html) {
  const fragment = document.createRange().createContextualFragment(html);
  el.replaceWith(fragment);
}

async function fetchRecipeCardHtml(headers, recipeId) {
  if (htmlByRecipeId.has(recipeId)) {
    return htmlByRecipeId.get(recipeId);
  }
  if (fetchInFlight.has(recipeId)) {
    return fetchInFlight.get(recipeId);
  }

  const promise = fetchSsrHtml(RECIPE_CARD_HTML_ENDPOINT, headers, {
    recipe_id: recipeId,
    store_id: getPosExtId(),
    variant: String(DISPLAY_RECIPE_VARIANT),
  }, getSsrBaseUrl())
    .then((html) => {
      htmlByRecipeId.set(recipeId, html);
      fetchInFlight.delete(recipeId);
      return html;
    })
    .catch((err) => {
      fetchInFlight.delete(recipeId);
      throw err;
    });

  fetchInFlight.set(recipeId, promise);
  return promise;
}

async function fetchAndReplaceCard(headers, el, recipeId) {
  try {
    const html = await fetchRecipeCardHtml(headers, recipeId);
    replaceCard(el, html);
  } catch (err) {
    unmarkPending(el);
    console.warn(
      `[CoursesU] recipe-card-by-id SSR failed for recipe ${recipeId}`,
      err
    );
  }
}

function ensureStylesLoaded() {
  if (stylesLoaded) {
    return;
  }
  stylesLoaded = true;
  fetchSsrStyles(
    `${RECIPE_CARD_STYLES_ENDPOINT}?variant=${DISPLAY_RECIPE_VARIANT}`,
    getSsrBaseUrl()
  );
}

function ensureObserver(headers) {
  if (observerStarted) {
    return;
  }
  observerStarted = true;
  lastHeaders = headers;

  new MutationObserver(() => {
    clearTimeout(injectTimeout);
    injectTimeout = setTimeout(() => injectRecipeCardsById(lastHeaders), 100);
  }).observe(document.body, { attributes: false, childList: true, subtree: true });
}

/**
 * Replace legacy webc-miam-recipe-card tags (with mealz recipe-id) by SSR recipe cards.
 * Runs on any page; watches the DOM for dynamically inserted tags.
 */
export async function injectRecipeCardsById(headers) {
  lastHeaders = headers;
  ensureObserver(headers);

  const pendingCards = collectPendingCards();
  if (pendingCards.length === 0) {
    return;
  }

  ensureStylesLoaded();

  const cardsByRecipeId = new Map();
  for (const el of pendingCards) {
    const recipeId = getRecipeId(el);
    if (!recipeId) {
      continue;
    }
    markPending(el, recipeId);
    if (!cardsByRecipeId.has(recipeId)) {
      cardsByRecipeId.set(recipeId, []);
    }
    cardsByRecipeId.get(recipeId).push(el);
  }

  await Promise.all(
    [...cardsByRecipeId.entries()].flatMap(([recipeId, elements]) =>
      elements.map((el) => fetchAndReplaceCard(headers, el, recipeId))
    )
  );
}
